/**
 * JournalScene — the player's primary "menu" (B-010).
 *
 * Per GDD §14.3, the journal is the primary menu of the game. J-key opens
 * this overlay; tabs render the player's accumulated Whispers, Relics,
 * Recipes, Pet, Visitors, Home Stage, Rumors, and Expeditions. Whispers +
 * Rumors are populated from the server's `JournalSnapshot` (B-011); the
 * Recipes tab is client-side from the recipe catalog + session discovery.
 *
 * Wire pattern (mirrors `SleepOverlayScene`): the overlay launches with
 * the parent scene paused; the parent supplies a `JournalSceneDeps` bundle
 * via the scene-launch payload (`sendMessage`, `dismiss`). On open, we
 * request a fresh snapshot via `MSG_JOURNAL_REQUEST_SNAPSHOT`; the parent
 * scene routes the `MSG_JOURNAL_SNAPSHOT` reply back via the `pushSnapshot`
 * registry contract below.
 *
 * Esc / J again / explicit "close" button all dismiss the overlay.
 *
 * No combat-stat chrome by design (B-010 hard constraint). Hand-lettered
 * typography vibe — Almendra serif throughout, courtesy of `journalTheme`.
 */

import * as Phaser from 'phaser';
import {
  MSG_JOURNAL_REQUEST_SNAPSHOT,
  type JournalSnapshot,
  type VocationKey,
} from '@game/shared';
import { JOURNAL_SCENE_KEY, PAINTED_WORLD_COORDINATOR_SCENE_KEY } from './sceneKeys';
import {
  JOURNAL_TABS,
  JOURNAL_TAB_TITLES,
  buildAllJournalTabs,
  type JournalTabId,
  type JournalTabState,
  type LocalJournalEntry,
  type RecipesTabContext,
} from '../journal/journalDispatch';
import {
  JOURNAL_BG_DEEP,
  JOURNAL_FONT,
  JOURNAL_FONT_SIZE,
  JOURNAL_INK,
  JOURNAL_INK_HOVER,
  JOURNAL_INK_MUTED,
  JOURNAL_LAYOUT,
  JOURNAL_PAPER_EDGE,
  JOURNAL_PIN,
  JOURNAL_VEIL,
  JOURNAL_VEIL_ALPHA,
} from '../journal/journalTheme';
import { drawPaperSlip } from '../ui/c5/paperMaterial';
import { getString } from '../ui/locale';
import { renderWhispersTab } from '../journal/WhispersTab';
import { renderRelicsTab } from '../journal/RelicsTab';
import { renderRecipesTab } from '../journal/RecipesTab';
import { renderRumorsTab } from '../journal/RumorsTab';
import { renderPetTab } from '../journal/PetTab';
import { renderVisitorsTab } from '../journal/VisitorsTab';
import { renderHomeStageTab } from '../journal/HomeStageTab';
import { renderExpeditionLogTab } from '../journal/ExpeditionLogTab';

/** Minimal contract the overlay needs from its parent world scene. The
 *  parent supplies these via `scene.launch(JOURNAL_SCENE_KEY, deps)`. */
export interface JournalSceneDeps {
  /** Send a typed Colyseus message. May be a no-op when the room is gone. */
  readonly sendMessage: (type: string, payload: Record<string, unknown>) => void;
  /** Dismiss the overlay + resume the parent. Provided so the overlay does
   *  not need to know whether the parent was paused. */
  readonly dismiss: () => void;
  /** Optional initial snapshot — when the parent already has a fresh
   *  snapshot in hand (e.g. just received `MSG_JOURNAL_WHISPER_GRANTED`),
   *  it can pass it here so the scene renders without an extra round-trip. */
  readonly initialSnapshot?: JournalSnapshot | null;
}

const SCROLL_SPEED = 24;

export class JournalScene extends Phaser.Scene {
  public static readonly KEY = JOURNAL_SCENE_KEY;

  private deps: JournalSceneDeps | null = null;
  private snapshot: JournalSnapshot | null = null;
  private tabStates: Record<JournalTabId, JournalTabState> | null = null;
  private activeTab: JournalTabId = 'whispers';

  private contentContainer: Phaser.GameObjects.Container | null = null;
  private contentMask: Phaser.Display.Masks.GeometryMask | null = null;
  private tabLabels: Map<JournalTabId, Phaser.GameObjects.Text> = new Map();
  private tabUnderline: Phaser.GameObjects.Graphics | null = null;
  private scrollOffset = 0;

  /** Client-local journal entries from festival lifecycle events (AGE-1199). */
  private localEntries: LocalJournalEntry[] = [];

  /** Recipe discovery context read from game.registry on init (F8). */
  private recipesContext: RecipesTabContext | undefined = undefined;

  constructor() {
    super({ key: JOURNAL_SCENE_KEY });
  }

  init(data: JournalSceneDeps | undefined): void {
    this.deps = data ?? null;
    this.snapshot = data?.initialSnapshot ?? null;
    this.activeTab = 'whispers';
    this.scrollOffset = 0;
    // Restore festival entries from the game registry (AGE-1199) so they
    // survive journal open/close cycles within the same session.
    this.localEntries = (this.game.registry.get('festival.journalEntries') as LocalJournalEntry[] | undefined) ?? [];

    // Recipe discovery context from game.registry (F8)
    const discoveredIds = (this.game.registry.get('recipes.discoveredIds') as Set<string> | undefined) ?? new Set<string>();
    const playerVocation = (this.game.registry.get('player.vocation') as VocationKey | undefined) ?? null;
    this.recipesContext = { discoveredRecipeIds: discoveredIds, playerVocation };
  }

  create(): void {
    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;

    // Indigo veil — the console dimming behind the spread. Also blocks input
    // to the paused world scene.
    const backdrop = this.add
      .rectangle(cx, cy, this.scale.width, this.scale.height, JOURNAL_VEIL, JOURNAL_VEIL_ALPHA)
      .setInteractive();
    backdrop.setScrollFactor(0);

    this.renderJournalPanel();
    this.renderTitle();
    this.renderTabStrip();
    this.renderCloseButton();
    this.setupContentArea();
    this.setupKeys();
    this.setupSnapshotListener();
    this.setupFestivalListener();

    // Compute tab states from whatever snapshot we have (possibly null) and
    // render the active tab. Then request a fresh snapshot — the listener
    // re-renders on arrival.
    this.recomputeTabs();
    this.renderActiveTab();
    this.requestSnapshot();

    // Fade-in transition
    this.cameras.main.setAlpha(0);
    this.tweens.add({
      targets: this.cameras.main,
      alpha: 1,
      duration: 250,
      ease: 'Cubic.easeOut',
    });
  }

  /** The journal reads as an open paper spread: two cream pages, a shaded
   *  gutter down the centre fold, and a soft shadow onto the indigo veil. */
  private renderJournalPanel(): void {
    const cx = this.scale.width / 2;
    const panelW = JOURNAL_LAYOUT.CONTENT_WIDTH + 48;
    const panelH = JOURNAL_LAYOUT.CONTENT_BOTTOM_Y - 10;
    const left = cx - panelW / 2;
    const g = this.add.graphics();

    drawPaperSlip(g, left, 10, panelW, panelH, { radius: 6, shadow: 6 });

    // Centre fold — a soft gutter so it reads as a spread, not a sheet.
    g.fillStyle(JOURNAL_PAPER_EDGE, 0.16);
    g.fillRect(cx - 5, 14, 10, panelH - 8);
    g.lineStyle(1, JOURNAL_PAPER_EDGE, 0.4);
    g.lineBetween(cx, 16, cx, panelH + 4);

    // Inner rule, drawn a hair inside the deckled edge.
    g.lineStyle(0.5, JOURNAL_PAPER_EDGE, 0.45);
    g.strokeRoundedRect(left + 5, 15, panelW - 10, panelH - 10, 5);

  }

  /** Title — top-center, matches the painterly chrome of other overlays. */
  private renderTitle(): void {
    const cx = this.scale.width / 2;
    this.add
      .text(cx, 32, getString('journal.title'), {
        fontFamily: JOURNAL_FONT,
        fontSize: JOURNAL_FONT_SIZE.TITLE,
        color: JOURNAL_INK,
      })
      .setOrigin(0.5, 0.5);
  }

  /** Tab strip — labels in a row, click or arrow-key to swap. */
  private renderTabStrip(): void {
    const totalWidth = JOURNAL_LAYOUT.CONTENT_WIDTH;
    const tabSpacing = totalWidth / JOURNAL_TABS.length;
    const startX = this.scale.width / 2 - totalWidth / 2 + tabSpacing / 2;
    const y = JOURNAL_LAYOUT.TAB_STRIP_Y;

    JOURNAL_TABS.forEach((tabId, idx) => {
      const x = startX + idx * tabSpacing;
      const hit = this.add
        .rectangle(x, y, tabSpacing - 6, 28, 0x000000, 0)
        .setOrigin(0.5, 0.5)
        .setInteractive({ useHandCursor: true });
      const label = this.add.text(x, y, JOURNAL_TAB_TITLES[tabId], {
        fontFamily: JOURNAL_FONT,
        fontSize: JOURNAL_FONT_SIZE.TAB,
        color: JOURNAL_INK_MUTED,
      });
      label.setOrigin(0.5, 0.5);

      hit.on('pointerover', () => {
        if (this.activeTab !== tabId) label.setColor(JOURNAL_INK_HOVER);
      });
      hit.on('pointerout', () => {
        if (this.activeTab !== tabId) label.setColor(JOURNAL_INK_MUTED);
      });
      hit.on('pointerdown', () => this.setActiveTab(tabId));

      this.tabLabels.set(tabId, label);
    });

    this.tabUnderline = this.add.graphics();
    this.refreshTabHighlights();
  }

  /** Close button — top-right of the journal column. */
  private renderCloseButton(): void {
    const x = this.scale.width / 2 + JOURNAL_LAYOUT.CONTENT_WIDTH / 2;
    // Below the C5 day-wheel's overlap zone — the DOM dial trespasses the
    // plate's top-right corner and would cover a label at the old y=32.
    const y = 96;
    const hit = this.add
      .rectangle(x, y, 80, 28, 0x000000, 0)
      .setOrigin(1, 0.5)
      .setInteractive({ useHandCursor: true });
    const label = this.add.text(x, y, getString('journal.close'), {
      fontFamily: JOURNAL_FONT,
      fontSize: JOURNAL_FONT_SIZE.TAB,
      color: JOURNAL_INK_MUTED,
      fontStyle: 'italic',
    });
    label.setOrigin(1, 0.5);

    hit.on('pointerover', () => label.setColor(JOURNAL_INK_HOVER));
    hit.on('pointerout', () => label.setColor(JOURNAL_INK_MUTED));
    hit.on('pointerdown', () => this.dismiss());
  }

  /** Content scroll area — masked container so long lists clip cleanly. */
  private setupContentArea(): void {
    const left = this.scale.width / 2 - JOURNAL_LAYOUT.CONTENT_WIDTH / 2 + JOURNAL_LAYOUT.CONTENT_PAD_X;
    const top = JOURNAL_LAYOUT.CONTENT_TOP_Y;
    const containerY = top;
    const container = this.add.container(left, containerY);
    container.setScrollFactor(0);
    this.contentContainer = container;

    const maskW = JOURNAL_LAYOUT.CONTENT_WIDTH - JOURNAL_LAYOUT.CONTENT_PAD_X * 2;
    const maskH = JOURNAL_LAYOUT.CONTENT_BOTTOM_Y - JOURNAL_LAYOUT.CONTENT_TOP_Y;
    const maskShape = this.make.graphics({}, false);
    maskShape.fillStyle(0xffffff, 1);
    maskShape.fillRect(left, top, maskW, maskH);
    const mask = maskShape.createGeometryMask();
    container.setMask(mask);
    this.contentMask = mask;

    // Scroll the content with mouse wheel.
    this.input.on('wheel', (
      _pointer: Phaser.Input.Pointer,
      _objects: Phaser.GameObjects.GameObject[],
      _dx: number,
      dy: number,
    ) => {
      this.applyScroll(dy > 0 ? SCROLL_SPEED : -SCROLL_SPEED);
    });
  }

  /** Keyboard wiring — Esc/J close; left/right swap tabs; up/down scroll. */
  private setupKeys(): void {
    const kb = this.input.keyboard;
    if (!kb) return;
    const esc = kb.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
    const jKey = kb.addKey(Phaser.Input.Keyboard.KeyCodes.J);
    const left = kb.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT);
    const right = kb.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT);
    const up = kb.addKey(Phaser.Input.Keyboard.KeyCodes.UP);
    const down = kb.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN);

    esc.on('down', () => this.dismiss());
    jKey.on('down', () => this.dismiss());
    left.on('down', () => this.cycleTab(-1));
    right.on('down', () => this.cycleTab(1));
    up.on('down', () => this.applyScroll(-SCROLL_SPEED));
    down.on('down', () => this.applyScroll(SCROLL_SPEED));
  }

  /** Hook the scene to live whisper/rumor broadcasts on the parent so an
   *  in-flight grant lands without a manual refresh. The parent scene is
   *  responsible for forwarding via the `journal:snapshot` game-event. */
  private setupSnapshotListener(): void {
    const handler = (snapshot: JournalSnapshot | null) => {
      if (!snapshot) return;
      this.snapshot = snapshot;
      this.recomputeTabs();
      this.renderActiveTab();
    };
    this.game.events.on('journal:snapshot', handler);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.game.events.off('journal:snapshot', handler);
    });
  }

  /** AGE-1199 — Listen for festival journal entries emitted by FestivalToast.
   *  OverworldScene stores the entries in game.registry; we just re-render. */
  private setupFestivalListener(): void {
    const handler = () => {
      this.localEntries = (this.game.registry.get('festival.journalEntries') as LocalJournalEntry[] | undefined) ?? [];
      this.recomputeTabs();
      this.renderActiveTab();
    };
    this.game.events.on('festival:journal-entry', handler);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.game.events.off('festival:journal-entry', handler);
    });
  }

  /** Send the snapshot-pull request to the server via the parent. */
  private requestSnapshot(): void {
    this.deps?.sendMessage(MSG_JOURNAL_REQUEST_SNAPSHOT, {});
  }

  /** Recompute the cached tab states from `this.snapshot`. */
  private recomputeTabs(): void {
    this.tabStates = buildAllJournalTabs(this.snapshot, this.localEntries, this.recipesContext);
  }

  private setActiveTab(tabId: JournalTabId): void {
    if (this.activeTab === tabId) return;
    this.activeTab = tabId;
    this.scrollOffset = 0;
    this.refreshTabHighlights();
    this.renderActiveTab();
  }

  private cycleTab(delta: number): void {
    const idx = JOURNAL_TABS.indexOf(this.activeTab);
    const next = (idx + delta + JOURNAL_TABS.length) % JOURNAL_TABS.length;
    this.setActiveTab(JOURNAL_TABS[next]);
  }

  private refreshTabHighlights(): void {
    for (const [tabId, label] of this.tabLabels) {
      label.setColor(tabId === this.activeTab ? JOURNAL_INK : JOURNAL_INK_MUTED);
    }
    if (this.tabUnderline) {
      this.tabUnderline.clear();
      const activeLabel = this.tabLabels.get(this.activeTab);
      if (activeLabel) {
        const lx = activeLabel.x - activeLabel.width * activeLabel.originX;
        const ly = activeLabel.y + activeLabel.height * (1 - activeLabel.originY) + 4;
        // Brush underline under the open tab — hanko red, the authored mark.
        this.tabUnderline.lineStyle(2, JOURNAL_PIN, 0.85);
        this.tabUnderline.lineBetween(lx, ly, lx + activeLabel.width, ly);
      }
    }
  }

  /** Dispatch to the right tab module. */
  private renderActiveTab(): void {
    if (!this.contentContainer || !this.tabStates) return;
    const state = this.tabStates[this.activeTab];
    switch (this.activeTab) {
      case 'whispers':
        renderWhispersTab(this, this.contentContainer, state);
        break;
      case 'relics':
        renderRelicsTab(this, this.contentContainer, state);
        break;
      case 'recipes':
        renderRecipesTab(this, this.contentContainer, state);
        break;
      case 'rumors':
        renderRumorsTab(this, this.contentContainer, state);
        break;
      case 'pet':
        renderPetTab(this, this.contentContainer, state);
        break;
      case 'visitors':
        renderVisitorsTab(this, this.contentContainer, state);
        break;
      case 'home-stage':
        renderHomeStageTab(this, this.contentContainer, state);
        break;
      case 'expedition-log':
        renderExpeditionLogTab(this, this.contentContainer, state);
        break;
    }
    this.scrollOffset = 0;
    this.applyScroll(0);
  }

  /** Scroll the content container vertically, clamped so it can't run past
   *  its content. The container's y-position is the absolute screen y of
   *  its origin; we keep `scrollOffset` as the delta. */
  private applyScroll(deltaY: number): void {
    if (!this.contentContainer) return;
    this.scrollOffset = Math.max(0, this.scrollOffset + deltaY);
    // Clamp to content bottom so the player can't scroll into the void.
    const maxScroll = this.estimateContentHeight();
    if (this.scrollOffset > maxScroll) this.scrollOffset = maxScroll;
    const baseY = JOURNAL_LAYOUT.CONTENT_TOP_Y - this.scrollOffset;
    this.contentContainer.setY(baseY);
  }

  private estimateContentHeight(): number {
    if (!this.contentContainer) return 0;
    const visibleH = JOURNAL_LAYOUT.CONTENT_BOTTOM_Y - JOURNAL_LAYOUT.CONTENT_TOP_Y;
    let totalH = 0;
    for (const child of this.contentContainer.list) {
      const obj = child as Phaser.GameObjects.GameObject & { y?: number; height?: number };
      const bottomY = (obj.y ?? 0) + (obj.height ?? 0);
      if (bottomY > totalH) totalH = bottomY;
    }
    return Math.max(0, totalH - visibleH);
  }

  /** Dismiss + resume the parent scene. The parent provides the dismiss
   *  callback so the journal does not need to know whether the parent was
   *  paused (vs. just hidden under a launched overlay). */
  private dismiss(): void {
    if (this.deps?.dismiss) {
      this.deps.dismiss();
      return;
    }
    // Fallback — best-effort resume of the overworld scene.
    this.scene.resume(PAINTED_WORLD_COORDINATOR_SCENE_KEY);
    this.scene.stop(JOURNAL_SCENE_KEY);
  }

  shutdown(): void {
    this.tabLabels.clear();
    this.contentContainer?.destroy(true);
    this.contentContainer = null;
    if (this.contentMask) {
      this.contentMask.destroy();
      this.contentMask = null;
    }
  }
}

// Backdrop colour exported for any future test that wants to assert the
// scene's background palette without instantiating Phaser.
export const JOURNAL_SCENE_BG = JOURNAL_BG_DEEP;
